// Sign-up, sign-in and the second factor (S-x) over Better Auth's endpoints on `api.` (`/api/auth/*`): a password
// signs in, e-mail codes verify the address and reset a forgotten password, and an optional second factor is an e-mail
// code or a TOTP app. Better Auth's errors are `{ code, message }` (not our `{ error }`): `authErrorOf` turns them into
// the keys the screens show. Codes and passwords are never stored or logged here.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ApiFailure, type ApiResult } from './api.ts';
import { ApiFailureError } from './queries.ts';
import { meQueryKey } from './session.ts';

export type TwoFactorMethod = 'totp' | 'otp';

/** What a screen says about a failed auth call (keys under `auth.errors`). */
export type AuthErrorKey =
  | 'credentials' | 'notVerified' | 'exists' | 'passwordShort' | 'codeInvalid' | 'codeExpired' | 'tooManyAttempts'
  | 'locked' | 'rateLimited' | 'network' | 'generic';

export function authErrorOf(f: ApiFailure): AuthErrorKey {
  if (f.status === 0) return 'network';
  if (f.status === 429) return 'rateLimited';
  const code = typeof f.body.code === 'string' ? f.body.code : '';
  switch (code) {
    case 'INVALID_EMAIL_OR_PASSWORD':
    case 'INVALID_PASSWORD':
      return 'credentials';
    case 'EMAIL_NOT_VERIFIED':
      return 'notVerified';
    case 'USER_ALREADY_EXISTS':
    case 'USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL':
      return 'exists';
    case 'PASSWORD_TOO_SHORT':
      return 'passwordShort';
    case 'INVALID_OTP':
    case 'INVALID_CODE':
    case 'INVALID_BACKUP_CODE':
      return 'codeInvalid';
    case 'OTP_EXPIRED':
    case 'OTP_HAS_EXPIRED':
    case 'INVALID_TWO_FACTOR_COOKIE':
      return 'codeExpired';
    case 'TOO_MANY_ATTEMPTS':
    case 'TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE':
      return 'tooManyAttempts';
    case 'ACCOUNT_TEMPORARILY_LOCKED':
      return 'locked';
    default:
      return 'generic';
  }
}

export const PASSWORD_MIN = 10; // Better Auth's `minPasswordLength` (apps/api/src/auth.ts)

/**
 * Where to go after signing in: `?para=` only when it is a path on this site (`/…`, never `//host` or a scheme) —
 * an open redirect would let a crafted link send a fresh session elsewhere.
 */
export function safeReturnTo(para: unknown, fallback = '/'): string {
  if (typeof para !== 'string' || !para.startsWith('/') || para.startsWith('//') || para.startsWith('/\\')) return fallback;
  return para;
}

// The address being verified travels to /cadastro/verificar in this tab only (never in the URL: links get shared/logged).
const PENDING = 'req.pendingEmail';
export function rememberPendingEmail(email: string): void {
  try {
    globalThis.sessionStorage?.setItem(PENDING, email);
  } catch {
    /* blocked storage: the verify screen asks for the address */
  }
}
export function pendingEmail(): string {
  try {
    return globalThis.sessionStorage?.getItem(PENDING) ?? '';
  } catch {
    return '';
  }
}
export function forgetPendingEmail(): void {
  try {
    globalThis.sessionStorage?.removeItem(PENDING);
  } catch {
    /* nothing kept */
  }
}

const ok = <T>(r: ApiResult<T>): T => {
  if (!r.ok) throw new ApiFailureError(r);
  return r.data;
};

export type SignInResult = { twoFactor: false } | { twoFactor: true; methods: TwoFactorMethod[] };

export const authApi = {
  signUp: async (b: { name: string; email: string; password: string }) => ok(await api.post('/api/auth/sign-up/email', b)),
  /** `email-verification` (sign-up) or `forget-password`; Better Auth answers 200 whether or not the address exists. */
  sendCode: async (email: string, type: 'email-verification' | 'forget-password') =>
    ok(await api.post('/api/auth/email-otp/send-verification-otp', { email, type })),
  verifyEmail: async (email: string, otp: string) => ok(await api.post('/api/auth/email-otp/verify-email', { email, otp })),
  resetPassword: async (email: string, otp: string, password: string) =>
    ok(await api.post('/api/auth/email-otp/reset-password', { email, otp, password })),
  signIn: async (email: string, password: string): Promise<SignInResult> => {
    const d = ok(await api.post<{ twoFactorRedirect?: boolean; twoFactorMethods?: TwoFactorMethod[] }>('/api/auth/sign-in/email', { email, password, rememberMe: true }));
    return d?.twoFactorRedirect ? { twoFactor: true, methods: d.twoFactorMethods?.length ? d.twoFactorMethods : ['otp'] } : { twoFactor: false };
  },
  sendTwoFactorCode: async () => ok(await api.post('/api/auth/two-factor/send-otp', {})),
  verifyTwoFactor: async (kind: TwoFactorMethod | 'backup', code: string, trustDevice: boolean) =>
    ok(await api.post(kind === 'totp' ? '/api/auth/two-factor/verify-totp' : kind === 'otp' ? '/api/auth/two-factor/verify-otp' : '/api/auth/two-factor/verify-backup-code', { code, trustDevice })),
  enableTwoFactor: async (password: string, method: TwoFactorMethod) =>
    ok(await api.post<{ method: TwoFactorMethod; totpURI?: string; backupCodes?: string[] }>('/api/auth/two-factor/enable', { password, method })),
  /** Confirms the authenticator app during set-up: only then is the second factor on. */
  confirmTotp: async (code: string) => ok(await api.post('/api/auth/two-factor/verify-totp', { code })),
  disableTwoFactor: async (password: string) => ok(await api.post('/api/auth/two-factor/disable', { password })),
  signOut: async () => ok(await api.post('/api/auth/sign-out', {})),
};

/** After any call that opens or closes a session: the session (and everything scoped to it) is re-read. */
export function useAuthMutation<V, R>(fn: (v: V) => Promise<R>, opts: { refreshSession?: boolean } = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      if (opts.refreshSession) await qc.invalidateQueries({ queryKey: meQueryKey });
    },
  });
}

/** The secret of an otpauth:// URI (base32), for typing into an authenticator app by hand. */
export function totpSecretOf(uri: string): string | null {
  try {
    return new URL(uri).searchParams.get('secret');
  } catch {
    return null;
  }
}
